import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth, useCan } from './lib/auth.js';
import { Banner, ErrorState, Loading, Pill, ToastProvider } from './components/ui.js';
import { TemplatesPage } from './pages/Templates.js';
import { TemplateEditorPage } from './pages/TemplateEditor.js';
import { RulesPage } from './pages/Rules.js';
import { DeployPage } from './pages/Deploy.js';
import { DeploymentsPage } from './pages/Deployments.js';
import { DeploymentDetailPage } from './pages/DeploymentDetail.js';
import { AuditLogPage } from './pages/AuditLog.js';
import { AdminPage } from './pages/Admin.js';
import { RunbookPage } from './pages/Runbook.js';
import { MySignaturePage } from './pages/MySignature.js';

/** Identities the dev picker offers, matching what the seed creates. */
const DEV_IDENTITIES = [
  { label: 'Admin', value: 'dev@localhost|Dev User|ADMIN' },
  { label: 'Marketing', value: 'sam.okafor@contoso.com|Sam Okafor|MARKETING' },
  { label: 'Approver', value: 'priya.raman@contoso.com|Priya Raman|APPROVER' },
  { label: 'IT', value: 'it.ops@contoso.com|IT Operations|IT' },
  { label: 'Viewer', value: 'auditor@contoso.com|Internal Audit|VIEWER' },
];

/**
 * The wordmark, in whichever version survives the current theme.
 *
 * Two files rather than a CSS filter: the brand blue reads at about 2.5:1 on a
 * dark surface, which is below the point where a logo is still legible, and
 * inverting a brand colour algorithmically produces something that is nobody's
 * brand. The white version is the same artwork with the opaque pixels recoloured,
 * so the letterforms and antialiasing are identical.
 *
 * <picture> does the swap natively, so there is no flash of the wrong one and it
 * works before any JavaScript has decided what the theme is.
 */
function BrandMark({ className }: { className?: string }): JSX.Element {
  return (
    <picture>
      <source srcSet="/brand/unrvld-logo-white.png" media="(prefers-color-scheme: dark)" />
      <img src="/brand/unrvld-logo.png" alt="UNRVLD" className={className} width={336} height={33} />
    </picture>
  );
}

/** The four-square Microsoft mark. Inline so the button needs no network request. */
function MicrosoftLogo(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="7" height="7" fill="#f25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7fba00" />
      <rect x="0" y="9" width="7" height="7" fill="#00a4ef" />
      <rect x="9" y="9" width="7" height="7" fill="#ffb900" />
    </svg>
  );
}

export function App(): JSX.Element {
  const { status, error, me, signIn, config } = useAuth();
  const can = useCan();

  if (status === 'loading') {
    return (
      <div className="gate">
        <div className="gate-card">
          <BrandMark className="gate-logo" />
          <Loading rows={2} label="Signing you in" />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="gate">
        <div className="gate-card">
          <BrandMark className="gate-logo" />
          <h1 className="gate-title">Signatures</h1>
          <ErrorState error={new Error(error ?? 'Sign-in failed')} />
          <button className="gate-button" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (status === 'signed-out' || !me) {
    return (
      <div className="gate">
        <div className="gate-card">
          <BrandMark className="gate-logo" />
          <h1 className="gate-title">Signatures</h1>
          <p className="gate-lead">
            Your company email signature, built from the directory. Sign in to copy yours, or to
            manage the templates everyone uses.
          </p>
          <button className="primary gate-button" onClick={() => void signIn()}>
            <MicrosoftLogo />
            Sign in with Microsoft
          </button>
          <p className="gate-foot">
            Use your UNRVLD account. If you have not signed in before, you will be set up
            automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <div className="shell">
        <Sidebar />
        <main className="main">
          <Routes>
            <Route
              path="/"
              element={<Navigate to={can('template:read') ? '/templates' : '/my-signature'} replace />}
            />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/templates/:id" element={<TemplateEditorPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/deploy" element={<DeployPage />} />
            <Route path="/deployments" element={<DeploymentsPage />} />
            <Route path="/deployments/:id" element={<DeploymentDetailPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/runbook" element={<RunbookPage />} />
            <Route path="/my-signature" element={<MySignaturePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </ToastProvider>
  );
}

function Sidebar(): JSX.Element {
  const { me, config, signOut, devMode, devIdentity, setDevIdentity } = useAuth();
  const can = useCan();

  const initials = (me?.displayName ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <nav className="nav">
      <div className="nav-brand">
        <BrandMark className="nav-logo" />
        <span className="nav-brand-sub">Signatures</span>
      </div>

      <NavLink to="/my-signature" className="nav-link">
        My signature
      </NavLink>

      {/*
        Someone with no permissions at all -- the STAFF default once the app is
        open to the tenant -- sees only their own signature. Showing them
        navigation that 403s would read as the app being broken.
      */}
      {can('template:read') && (
        <>
      <div className="nav-section">Design</div>
      <NavLink to="/templates" className="nav-link">
        Templates
      </NavLink>
      <NavLink to="/rules" className="nav-link">
        Assignment rules
      </NavLink>

      <div className="nav-section">Deployment</div>
      <NavLink
        to="/deploy"
        className="nav-link"
        aria-disabled={!can('deploy:execute')}
        title={can('deploy:execute') ? undefined : 'Your role cannot push to Exchange'}
      >
        New deployment
      </NavLink>
      <NavLink to="/deployments" className="nav-link">
        History
      </NavLink>

      <div className="nav-section">Governance</div>
      <NavLink to="/audit" className="nav-link">
        Audit trail
      </NavLink>
      <NavLink to="/admin" className="nav-link">
        Administration
      </NavLink>
        </>
      )}
      {/*
        Admin-only, because it documents steps most roles cannot perform and
        would only be confusing to them. The page itself repeats the check.
      */}
      {can('user:manage') && (
        <NavLink to="/runbook" className="nav-link">
          Runbook
        </NavLink>
      )}

      <div className="nav-foot">
        {config.features.globalDryRun && (
          <div style={{ marginBottom: 8 }}>
            <Pill tone="warn">dry-run mode</Pill>
          </div>
        )}

        {devMode && (
          <div style={{ marginBottom: 10 }}>
            <label className="field-label" htmlFor="dev-identity">
              Dev identity
            </label>
            <select
              id="dev-identity"
              value={devIdentity ?? DEV_IDENTITIES[0]!.value}
              onChange={(e) => setDevIdentity(e.target.value)}
            >
              {DEV_IDENTITIES.map((identity) => (
                <option key={identity.value} value={identity.value}>
                  {identity.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="nav-user">
          <span className="nav-avatar" aria-hidden="true">
            {initials}
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <div
              className="cell-strong"
              style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {me?.displayName}
            </div>
            <div className="tiny muted">{me?.role.toLowerCase()}</div>
          </span>
          <button className="ghost sm" onClick={() => void signOut()} title="Sign out">
            ⏻
          </button>
        </div>
      </div>
    </nav>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="page-body">
      <Banner tone="warn">
        <strong>Page not found</strong>
        <div className="tiny">
          <NavLink to="/templates">Back to templates</NavLink>
        </div>
      </Banner>
    </div>
  );
}
